/**
 * recordFlight — wraps the shot timeline with MediaRecorder start/stop.
 *
 * Capture order (strict):
 *   lock → pose START (verified) → resize → re-pose START → start recorder
 *   → hold at START → fly to END → hold at END → stop → then restore UI
 *
 * Never restore the viewport / unlock until the recorder has fully stopped,
 * or end-pose / resized frames leak into the file.
 */

import type { GlobeInstance } from "globe.gl";
import {
  animateShotFlight,
  finishShotArrival,
  lockShotCamera,
  poseAtShotStart,
  shotPoses,
  wait,
  type FlyToTarget,
} from "@web/features/globe/flyTo.ts";
import {
  SHOT_ASPECT_PIXELS,
  type ShotAspect,
  type ShotPresetValues,
} from "@web/features/globe/shotPreset.ts";

const CAPTURE_FPS = 30;
const TARGET_BITRATE = 8_000_000;
/** Extra frames at start pose after recorder starts — drops stale GPU/backbuffer. */
const CAPTURE_PREROLL_MS = 200;

export type RecordFlightHooks = {
  enterRecordViewport: (aspect: ShotAspect) => void | Promise<void>;
  exitRecordViewport: () => void | Promise<void>;
  enterCleanStage: () => void;
  exitCleanStage: () => void;
  getCanvas: () => HTMLCanvasElement | null;
};

/**
 * Prefer MP4/H.264 when the browser can encode it (recent Chrome/Edge often
 * can). Fall back to WebM — no ffmpeg in the app.
 */
export function pickRecorderMimeType(): string {
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4;codecs=avc1.42E01E",
    "video/mp4;codecs=avc1",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const t of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) {
      return t;
    }
  }
  return "video/webm";
}

export function isMp4Mime(mime: string): boolean {
  return mime.startsWith("video/mp4");
}

export async function recordFlight(
  globe: GlobeInstance,
  target: FlyToTarget,
  preset: ShotPresetValues,
  aspect: ShotAspect,
  hooks: RecordFlightHooks,
  signal?: AbortSignal,
): Promise<Blob> {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  const canvas = hooks.getCanvas();
  if (!canvas) throw new Error("Globe canvas not ready");

  hooks.enterCleanStage();
  const unlock = lockShotCamera(globe);
  const { start, end } = shotPoses(target, preset, aspect);

  try {
    // 1) Pose at zoomed-out START while still full-pane (cheap to verify).
    await poseAtShotStart(globe, start);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    // 2) Resize to record aspect, then force START again (resize can desync POV).
    await hooks.enterRecordViewport(aspect);
    await poseAtShotStart(globe, start);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    // 3) Only now open the capture stream — backbuffer should be START.
    const stream = canvas.captureStream(CAPTURE_FPS);
    const mimeType = pickRecorderMimeType();
    const chunks: BlobPart[] = [];
    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: TARGET_BITRATE,
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    const stopped = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType }));
      };
      recorder.onerror = () => {
        reject(new Error("MediaRecorder failed"));
      };
    });

    try {
      // No timeslice — one continuous cluster; avoids odd keyframe-at-head glitches.
      recorder.start();
      // Preroll: keep START in the file even if the first captured frame was stale.
      globe.pointOfView(start, 0);
      await wait(CAPTURE_PREROLL_MS);
      globe.pointOfView(start, 0);

      await wait(preset.holdBeforeMs);
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      // Re-assert start the instant before the tween (no leftover end pose).
      globe.pointOfView(start, 0);
      await wait(32);

      await animateShotFlight(globe, end, preset.durationMs);
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      await finishShotArrival(globe, target, preset);
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      await wait(preset.holdAfterMs);
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      if (recorder.state === "recording") {
        recorder.requestData();
        recorder.stop();
      }
      for (const track of stream.getTracks()) track.stop();
      // Wait for the blob BEFORE restoring viewport — late frames must not
      // include the post-shot UI resize / camera unlock.
      return await stopped;
    } catch (err) {
      if (recorder.state === "recording") recorder.stop();
      for (const track of stream.getTracks()) track.stop();
      throw err;
    }
  } finally {
    unlock();
    hooks.exitCleanStage();
    await hooks.exitRecordViewport();
  }
}

/** Preview: same camera timeline as record (start → hold → fly → hold). */
export async function previewShot(
  globe: GlobeInstance,
  target: FlyToTarget,
  preset: ShotPresetValues,
  aspect: ShotAspect,
): Promise<void> {
  const unlock = lockShotCamera(globe);
  try {
    const { start, end } = shotPoses(target, preset, aspect);
    await poseAtShotStart(globe, start);
    await wait(preset.holdBeforeMs);
    globe.pointOfView(start, 0);
    await wait(32);
    await animateShotFlight(globe, end, preset.durationMs);
    await finishShotArrival(globe, target, preset);
    await wait(preset.holdAfterMs);
  } finally {
    unlock();
  }
}

export function aspectPixelSize(aspect: ShotAspect): { width: number; height: number } {
  return SHOT_ASPECT_PIXELS[aspect];
}
